// Single stop-timeline row.
import type { DragEvent } from "react";
import {
  formatTimestamp,
  ghostBtnClass,
  primaryBtnClass,
} from "./logisticsRoutesShared";
import type { LogiStop, LogiStopKind } from "@/lib/logistics";

const STOP_KIND_LABELS: Record<LogiStopKind, string> = {
  plant_origin: "Plant origin",
  dealer_holding: "Dealer holding",
  end_customer: "End customer",
  service: "Service",
  other: "Other",
};

// Tiny per-row action button. Kept local to the row module since
// this is the only consumer; if a fourth surface ever needs the
// same small-button shape, hoist to logisticsRoutesShared.
function StopActionBtn({
  label,
  busy,
  disabled,
  onClick,
  variant = "ghost",
}: {
  label: string;
  busy: boolean;
  disabled?: boolean;
  onClick: () => void;
  variant?: "ghost" | "primary";
}) {
  const cls = variant === "primary" ? primaryBtnClass : ghostBtnClass;
  return (
    <button
      type="button"
      className={`${cls} text-[11px] px-2 py-1`}
      onClick={onClick}
      disabled={busy || disabled}
    >
      {busy ? `${label}…` : label}
    </button>
  );
}

// D3i evidence quick-capture button. <label> wrapping a hidden file
// input -- a real <button> can't trigger the OS file picker without
// the click handler routing through a ref to a hidden input, and the
// label-wrap pattern is simpler + mobile-friendly. `capture="environment"`
// prompts the rear camera on mobile (still falls through to the file
// picker on desktop). Server-side allowlist also rejects unsupported
// types, so the `accept` attribute is purely UX (less file-picker
// clutter for drivers).
const EVIDENCE_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

function StopEvidenceCaptureBtn({
  busy,
  disabled,
  onFile,
}: {
  busy: boolean;
  disabled?: boolean;
  onFile: (file: File) => void;
}) {
  const label = busy ? "Uploading…" : "Capture evidence";
  // Keep input controlled via key reset so selecting the same file
  // twice still fires onChange (browsers suppress duplicate selections
  // otherwise). Resetting on each render is wasteful but fine for a
  // single row; if this grows, hoist a useRef + e.target.value=""
  // pattern.
  return (
    <label
      className={`${ghostBtnClass} text-[11px] px-2 py-1 inline-flex items-center cursor-pointer ${busy || disabled ? "opacity-60 pointer-events-none" : ""}`}
    >
      {label}
      <input
        type="file"
        accept={EVIDENCE_ACCEPT}
        capture="environment"
        className="hidden"
        disabled={busy || disabled}
        onChange={(ev) => {
          const file = ev.target.files?.[0];
          ev.target.value = "";
          if (file) onFile(file);
        }}
      />
    </label>
  );
}

export type LogisticsRoutesStopRowProps = {
  stop: LogiStop;
  // D3g UI quick-action / admin-edit affordances + D3i evidence
  // capture. The row stays a pure presentation component; all
  // stateful work lives in the sibling useLogisticsRouteStopEdits
  // hook.
  isEditing: boolean;
  quickActionBusy: { stopId: string; action: "arrived" | "departed" | "evidence" } | null;
  onMarkArrived: () => void;
  onMarkDeparted: () => void;
  onCaptureEvidence: (file: File) => void;
  onStartEdit: () => void;
  reorder?: {
    canReorder: boolean;
    reorderBusy: boolean;
    isFirst: boolean;
    isLast: boolean;
    onMoveUp: () => void;
    onMoveDown: () => void;
    onDragStart: () => void;
    onDragOver: (e: DragEvent) => void;
    onDrop: () => void;
  };
};

export function LogisticsRoutesStopRow({
  stop,
  isEditing,
  quickActionBusy,
  onMarkArrived,
  onMarkDeparted,
  onCaptureEvidence,
  onStartEdit,
  reorder,
}: LogisticsRoutesStopRowProps) {
  const windowStart = formatTimestamp(stop.scheduled_window_start);
  const windowEnd = formatTimestamp(stop.scheduled_window_end);
  const arrived = formatTimestamp(stop.arrived_at);
  const departed = formatTimestamp(stop.departed_at);
  const evidence = formatTimestamp(stop.evidence_completed_at);
  const window =
    windowStart && windowEnd
      ? `${windowStart} → ${windowEnd}`
      : windowStart
        ? `from ${windowStart}`
        : windowEnd
          ? `by ${windowEnd}`
          : null;
  const arrivedBusy =
    quickActionBusy?.stopId === stop.id && quickActionBusy.action === "arrived";
  const departedBusy =
    quickActionBusy?.stopId === stop.id && quickActionBusy.action === "departed";
  const evidenceBusy =
    quickActionBusy?.stopId === stop.id && quickActionBusy.action === "evidence";
  // Disable sibling quick-action buttons while one is in flight on
  // this row, regardless of which action it is, so a hammered driver
  // can't double-fire arrived + evidence in the same tap (evidence
  // upload is slow -- network + bytes -- so this guard matters most
  // for it).
  const otherBusy = quickActionBusy?.stopId === stop.id;
  return (
    <li
      className="flex flex-wrap items-start gap-2 py-3"
      draggable={!!reorder?.canReorder}
      onDragStart={() => reorder?.onDragStart()}
      onDragOver={(e) => reorder?.onDragOver(e)}
      onDrop={(e) => {
        e.preventDefault();
        reorder?.onDrop();
      }}
    >
      <div className="rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200 w-7 h-7 flex items-center justify-center text-xs font-semibold shrink-0">
        {stop.sequence_index}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
          {STOP_KIND_LABELS[stop.stop_kind] ?? stop.stop_kind}
        </div>
        {stop.recipient_org_id ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            Dealer org: <code>{stop.recipient_org_id}</code>
          </div>
        ) : null}
        {stop.recipient_contact_id ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            Contact: <code>{stop.recipient_contact_id}</code>
          </div>
        ) : null}
        {window ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">Window: {window}</div>
        ) : null}
        {arrived ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">Arrived {arrived}</div>
        ) : null}
        {departed ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">Departed {departed}</div>
        ) : null}
        {evidence || stop.evidence_url ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">
            Evidence: {evidence ?? "(awaiting capture)"}{" "}
            {stop.evidence_url ? (
              <a
                href={stop.evidence_url}
                target="_blank"
                rel="noreferrer"
                className="text-indigo-600 underline"
              >
                file
              </a>
            ) : null}
          </div>
        ) : null}
        {stop.notes ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">{stop.notes}</div>
        ) : null}
        {/* Action row: quick-actions for the driver flow + Edit for the
            admin form. Hidden while this row's edit form is open
            (the form has its own Save / Cancel buttons). */}
        {isEditing ? null : (
          <div className="mt-2 flex flex-wrap gap-2">
            {stop.arrived_at ? null : (
              <StopActionBtn
                label="Mark arrived"
                busy={arrivedBusy}
                disabled={otherBusy && !arrivedBusy}
                onClick={onMarkArrived}
                variant="primary"
              />
            )}
            {stop.departed_at ? null : (
              <StopActionBtn
                label="Mark departed"
                busy={departedBusy}
                disabled={otherBusy && !departedBusy}
                onClick={onMarkDeparted}
              />
            )}
            {/* D3i evidence quick-capture. Always offered (even when
                evidence_completed_at is set) so a driver can
                re-capture / replace a bad photo without dropping into
                the admin edit form. The action overwrites
                evidence_url + restamps evidence_completed_at = now. */}
            <StopEvidenceCaptureBtn
              busy={evidenceBusy}
              disabled={otherBusy && !evidenceBusy}
              onFile={onCaptureEvidence}
            />
            <StopActionBtn
              label="Edit"
              busy={false}
              disabled={otherBusy}
              onClick={onStartEdit}
            />
            {reorder?.canReorder ? (
              <>
                <StopActionBtn
                  label="↑"
                  busy={reorder.reorderBusy}
                  disabled={reorder.isFirst || reorder.reorderBusy}
                  onClick={reorder.onMoveUp}
                />
                <StopActionBtn
                  label="↓"
                  busy={reorder.reorderBusy}
                  disabled={reorder.isLast || reorder.reorderBusy}
                  onClick={reorder.onMoveDown}
                />
              </>
            ) : null}
          </div>
        )}
      </div>
    </li>
  );
}
